import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';

import { BadgeIcon } from '../SitterSearchCard/BadgesRow';
import { SearchCardBadge } from '../SitterSearchCard/utils';

const CONNECTION_DISPLAY_LIMIT = 16;
const GROUP_LIST_LIMIT = 1;
const MOBILE_WIDTH = 992;

interface ConnectionPerson {
  id?: number;
  name?: string;
  pictureUrl?: string;
}

interface ConnectionGroup {
  id?: number;
  name?: string;
  pictureUrl?: string;
  memberCount?: number;
}

export interface ParentSummaryParent {
  firstName?: string;
  affiliations?: Array<{ id?: number; affiliation?: { name?: string } }>;
  partnerBadge?: { label?: string; type?: string };
  covidBadge?: { consented?: boolean };
  badges?: SearchCardBadge[];
  relationship?: {
    connections?: {
      indirect?: ConnectionPerson[];
      affiliation?: ConnectionGroup[];
      company?: ConnectionGroup[];
      school?: ConnectionGroup[];
      localGroup?: ConnectionGroup[];
    };
  };
}

interface Props {
  parent?: ParentSummaryParent | null;
  minimized?: boolean;
}

const isMobileViewport = (): boolean =>
  (window.outerWidth || window.screen.width) < MOBILE_WIDTH;

const byMemberCountDesc = (a: ConnectionGroup, b: ConnectionGroup): number =>
  (b.memberCount ?? 0) - (a.memberCount ?? 0);

export const ParentSummary = ({ parent, minimized }: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(!minimized && !isMobileViewport());
  const [showMoreFaces, setShowMoreFaces] = useState(false);

  if (!parent) return null;

  const affiliations = parent.affiliations ?? [];
  const connections = parent.relationship?.connections;
  const indirect = connections?.indirect ?? [];
  const companies = [...(connections?.company ?? [])].sort(byMemberCountDesc);
  const groups = [
    ...[...(connections?.school ?? [])].sort(byMemberCountDesc),
    ...[...(connections?.affiliation ?? [])].sort(byMemberCountDesc),
    ...[...(connections?.localGroup ?? [])].sort(byMemberCountDesc),
  ];
  const badges = parent.badges ?? [];
  const showConnections = indirect.length > 0 || groups.length > 0 || companies.length > 0;
  const hasContent =
    showConnections ||
    affiliations.length > 0 ||
    !!parent.partnerBadge ||
    !!parent.covidBadge?.consented ||
    badges.length > 0;

  if (!hasContent) return null;

  const covidText = t('components.parentSummary.covidVaccine');
  const covidTooltip = t('components.parentSummary.covidVaccineTooltip');
  const visibleIndirect = showMoreFaces ? indirect : indirect.slice(0, CONNECTION_DISPLAY_LIMIT);
  const hiddenFaceCount = indirect.length - CONNECTION_DISPLAY_LIMIT;

  return (
    <div id='parent-summary' className='bg-neutral-50 rounded-lg p-4 mt-4'>
      <div className='flex justify-between items-center'>
        <h3 className='text-lg font-semibold'>
          {t('components.parentSummary.about', { name: parent.firstName ?? '' })}
        </h3>
        <button
          type='button'
          aria-expanded={isOpen}
          aria-label={t('components.parentSummary.toggleAriaLabel')}
          onClick={(): void => setIsOpen((open) => !open)}
          className='text-neutral-500 px-2 py-1'
        >
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </button>
      </div>
      {isOpen && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-3'>
          <div className='bg-white border-2 border-sapphire-50 rounded-[20px] break-words p-[15px] mb-[15px] md:col-span-2'>
            {showConnections && (
              <div className='mb-3'>
                {(indirect.length > 0 || groups.length > 0) && (
                  <h4 className='font-semibold mb-2'>
                    {t('components.parentSummary.youBothKnow')}
                  </h4>
                )}
                {indirect.length > 0 && (
                  <>
                    <div className='flex flex-wrap gap-1'>
                      {visibleIndirect.map((person, index) =>
                        person.pictureUrl ? (
                          <img
                            key={person.id ?? `${person.pictureUrl}-${index}`}
                            src={person.pictureUrl}
                            alt={person.name ?? ''}
                            title={person.name ?? ''}
                            className='w-10 h-10 rounded-full object-cover'
                          />
                        ) : null,
                      )}
                    </div>
                    {hiddenFaceCount > 0 && (
                      <button
                        type='button'
                        onClick={(): void => setShowMoreFaces((show) => !show)}
                        className='text-neutral-500 text-sm mt-2 underline'
                      >
                        {showMoreFaces
                          ? t('components.parentSummary.showLess')
                          : t('components.parentSummary.otherFriends', { count: hiddenFaceCount })}
                      </button>
                    )}
                  </>
                )}
                {indirect.length === 0 && (
                  <div className='space-y-2'>
                    {companies.map((company, index) => (
                      <div key={company.id ?? `company-${index}`} className='flex items-center gap-2'>
                        <FontAwesomeIcon icon={faUserGroup} className='text-neutral-500' />
                        <span className='text-sm'>
                          {t('components.parentSummary.familiesFromCompany', {
                            count: company.memberCount ?? 0,
                          })}
                        </span>
                        {company.pictureUrl && (
                          <img
                            src={company.pictureUrl}
                            alt=''
                            className='w-6 h-6 rounded object-cover'
                          />
                        )}
                      </div>
                    ))}
                    {groups.slice(0, GROUP_LIST_LIMIT).map((group, index) => (
                      <div key={group.id ?? `group-${index}`} className='flex items-center gap-2'>
                        <FontAwesomeIcon icon={faUserGroup} className='text-neutral-500' />
                        <span className='text-sm'>
                          {t('components.parentSummary.familiesFromGroup', {
                            count: group.memberCount ?? 0,
                            name: group.name ?? '',
                          })}
                        </span>
                      </div>
                    ))}
                    {groups.length > GROUP_LIST_LIMIT && (
                      <div className='text-sm text-neutral-600'>
                        {t('components.parentSummary.moreGroups', {
                          count: groups.length - GROUP_LIST_LIMIT,
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {affiliations.length > 0 && (
              <div className='mb-3'>
                <div className='font-semibold mb-2'>
                  {t('components.parentSummary.affiliations')}
                </div>
                <div>
                  {affiliations.map((entry, index) => (
                    <span key={entry.id ?? `${entry.affiliation?.name ?? ''}-${index}`}>
                      {entry.affiliation?.name}
                      {index < affiliations.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className='bg-white border-2 border-sapphire-50 rounded-[20px] break-words p-[15px] mb-[15px]'>
            {parent.partnerBadge && (
              <div className='mb-3 flex items-center'>
                <BadgeIcon badge={{ type: 'corporate', label: parent.partnerBadge.label }} />
                <span className='text-sm'>{parent.partnerBadge.type}</span>
              </div>
            )}
            {parent.covidBadge?.consented && (
              <div className='mb-3 flex items-center'>
                <BadgeIcon badge={{ type: 'covidBadge', label: covidTooltip }} />
                <span className='text-sm'>{covidText}</span>
              </div>
            )}
            {badges.map((badge) => (
              <div key={`${badge.type ?? ''}-${badge.label ?? ''}`} className='mb-3 flex items-center'>
                <BadgeIcon badge={badge} />
                <span className='text-sm'>{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
